import { EMPTY_PRICES } from "@apac/components/checkout";
import type { Fees, PriceQuoteV2, Rooms } from "@b2bportal/lodging-api";
import { Product, type Prices } from "@b2bportal/purchase-api";
import {
  CartSelectors,
  getCartQuoteBreakdownProducts,
  LodgingSelectors,
  type LodgingStateWithoutValue,
} from "@checkout/states";
import type { CartQuoteBreakdownProducts } from "@checkout/states/Cart/CartUpdate/selectors";
import type { CfarUpdateStateWithoutValue as HtsCfarUpdateState } from "@checkout/states/Products/HtsLodgingCFAR/selectors";
import type { CfarUpdateStateWithoutValue as LegacyCfarUpdateState } from "@checkout/states/Products/LodgingCFARV1/selectors";

type CfarUpdateStateWithoutValue = LegacyCfarUpdateState & HtsCfarUpdateState;
export type HotelPriceBreakdownItems = {
  // inlcluding hotel pay now and fintech prices
  payNowTotal?: Prices;
  roomPrice?: Prices;
  taxesAndFees?: Prices;

  stayNights?: number;
  // inlcluding hotel pay now, pay later, and fintech prices
  tripTotal?: Prices;
  // Pay later fees breakdown in user currency
  payLaterBreakdown?: Fees;
  // Pay later fees breakdown in hotel local currency
  payLaterBreakdownLocalCurrency?: Fees;

  bedTypes?: Rooms;
  cfarPrices?: Prices;
  roomName?: string;
  roomsCount?: number;
  totalDueToday?: Prices;
};

export const getHotelBreakdownItems = (
  state: LodgingStateWithoutValue & CfarUpdateStateWithoutValue
): HotelPriceBreakdownItems => {
  const quotedLodgingProduct = LodgingSelectors.getQuotedLodgingProduct(state);
  const quotedAllProductsTotal = getQuotedProductTotalPrice(state);
  const quotedLodgingPrice = (quotedLodgingProduct as PriceQuoteV2)?.pricingV1;
  const shoppedProduct = LodgingSelectors.getSelectedRoomProduct(state);
  const fintechProducts = getLodgingFintechProducts(state);
  // Prefer HTS CFAR over legacy CFAR if both exist
  const cfar = fintechProducts.HtsLodgingCfar ?? fintechProducts.LodgingCfar;

  // data from quote breakdown
  if (quotedLodgingPrice) {
    return {
      payNowTotal: quotedAllProductsTotal,
      roomPrice: quotedLodgingPrice.subTotal,
      taxesAndFees: quotedLodgingPrice.taxesAndFeesDetails?.payNow,
      stayNights: quotedLodgingPrice.stayNights,
      tripTotal: addPricesFiat(
        quotedLodgingPrice?.tripTotal,
        cfar?.totalPremiumAmount
      ),
      payLaterBreakdown:
        quotedLodgingPrice.taxesAndFeesDetails?.breakdown?.fees,
      payLaterBreakdownLocalCurrency:
        quotedLodgingPrice.taxesAndFeesDetails?.breakdown?.feesLocalCurrency ??
        quotedLodgingPrice.taxesAndFeesDetails?.breakdown?.fees,
      cfarPrices: cfar?.totalPremiumAmount,
      bedTypes: shoppedProduct?.bedTypes,
      roomName: shoppedProduct?.roomName,
      roomsCount: state.context.lodgingShop.selectedRoomsCount,
      totalDueToday: addPricesFiat(
        quotedLodgingPrice?.tripTotal,
        cfar?.totalPremiumAmount
      ),
    };
    // data from shopping
  } else {
    const stayNights = LodgingSelectors.getTotalNights(state);
    const lodgingPayNowTotal = shoppedProduct?.total;

    if (shoppedProduct != null) {
      return {
        payNowTotal: addPricesFiat(
          lodgingPayNowTotal,
          cfar?.totalPremiumAmount
        ),
        roomPrice: shoppedProduct.sellRate,
        stayNights,
        taxesAndFees: subtractPricesFiat(
          lodgingPayNowTotal,
          shoppedProduct.sellRate
        ),
        tripTotal: addPricesFiat(
          shoppedProduct.tripTotal,
          cfar?.totalPremiumAmount
        ),
        payLaterBreakdown: shoppedProduct.feeBreakdown,
        payLaterBreakdownLocalCurrency:
          shoppedProduct.feeBreakdownLocalCurrency ??
          shoppedProduct.feeBreakdown,
        cfarPrices: cfar?.totalPremiumAmount,
        bedTypes: shoppedProduct.bedTypes,
        roomName: shoppedProduct.roomName,
        roomsCount: state.context.lodgingShop.selectedRoomsCount,
        totalDueToday: addPricesFiat(
          shoppedProduct.total,
          cfar?.totalPremiumAmount
        ),
      };
    } else return {};
  }
};

export const addPricesFiat = (
  base?: Prices,
  extra?: Prices
): Prices | undefined => {
  if (base == null) {
    return extra;
  }

  return {
    ...base,
    fiat: {
      ...base.fiat,
      value: base.fiat.value + (extra?.fiat?.value ?? 0),
    },
  };
};

export const subtractPricesFiat = (
  base?: Prices,
  extra?: Prices
): Prices | undefined => {
  if (base == null) {
    return undefined;
  }

  return {
    ...base,
    fiat: {
      ...base.fiat,
      value: base.fiat.value - (extra?.fiat?.value ?? 0),
    },
  };
};

export const getLodgingFintechProducts = (
  state: LodgingStateWithoutValue & CfarUpdateStateWithoutValue
): CartQuoteBreakdownProducts => {
  const quoteProducts = getCartQuoteBreakdownProducts(state);
  const shopProducts = CartSelectors.getCartSelectedProducts(state);

  const merged = {
    ...shopProducts,
    ...quoteProducts,
  };

  // If both legacy and HTS CFAR exist, only use HTS
  if (merged[Product.HtsLodgingCfar] && merged[Product.LodgingCfar]) {
    delete merged[Product.LodgingCfar];
  }

  return merged;
};

export const getLodgingFintechTotal = (
  state: LodgingStateWithoutValue & CfarUpdateStateWithoutValue
): Prices | undefined => {
  const quoteProducts = getCartQuoteBreakdownProducts(state);
  const shopProducts = CartSelectors.getCartSelectedProducts(state);

  const products = {
    ...shopProducts,
    ...quoteProducts,
  };

  // Prefer HTS CFAR over legacy CFAR if both exist
  // TODO: add all if other fintechs added
  return (
    products.HtsLodgingCfar?.totalPremiumAmount ??
    products.LodgingCfar?.totalPremiumAmount
  );
};

/**
 * Get hotel pay now total, including room, fintech, etc.
 * Use quote breakdown if available otherwise use shopped price
 */
export const getHotelPayNowTotal = (
  state: LodgingStateWithoutValue & CfarUpdateStateWithoutValue
): Prices => {
  const quotedPaynowTotal = getQuotedProductTotalPrice(state);
  if (quotedPaynowTotal != null) {
    return quotedPaynowTotal;
  } else {
    const shopTotal = LodgingSelectors.getHotelRoomTotal(state);
    const fintechTotal = getLodgingFintechTotal(state);

    const total = addPricesFiat(shopTotal, fintechTotal);

    return total ?? EMPTY_PRICES;
  }
};

export const getQuotedProductTotalPrice = (
  state: LodgingStateWithoutValue
): Prices | undefined => {
  return state.context.cartQuote?.quoteBreakdown?.productTotal;
};

/**
 * Returns updated room price based on payNowTotal and taxesAndFees, this is needed
 * due to some currency conversion issues in the backend when the provider only provides
 * cost in USD, and we do currency conversion on the BE.
 */
export const getUpdatedHotelPriceBreakdownPrices = (
  state: LodgingStateWithoutValue & CfarUpdateStateWithoutValue
): HotelPriceBreakdownItems => {
  const breakdownItems = getHotelBreakdownItems(state);
  const { payNowTotal, roomPrice, taxesAndFees, cfarPrices } = breakdownItems;

  const updatedRoomPrice = subtractPricesFiat(
    subtractPricesFiat(payNowTotal, taxesAndFees),
    cfarPrices
  );

  return {
    ...breakdownItems,
    ...(updatedRoomPrice &&
      roomPrice && {
        roomPrice: updatedRoomPrice,
      }),
  };
};
